import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { api } from "../../helpers/utils";

const PAGE_SIZE = 50;
const STATUSES = ["pending", "approved", "rejected", "all"];
const QUICK_REASONS = [
  "UPI ID is invalid",
  "Suspicious activity",
  "Below minimum amount",
];

const formatNumber = (value, maximumFractionDigits = 2) =>
  Number(value).toLocaleString("en-IN", { maximumFractionDigits });

const formatMoney = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
    ? `₹${formatNumber(value)}`
    : "—";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const maskMobile = (value) => {
  if (!value) return "No mobile number";
  const mobile = String(value);
  if (mobile.length <= 4) return mobile;
  return `${"•".repeat(Math.min(mobile.length - 4, 6))}${mobile.slice(-4)}`;
};

const getMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export default function PepRedeemRequests() {
  const nav = useNavigate();
  const [activeStatus, setActiveStatus] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [lastId, setLastId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [revealedMobiles, setRevealedMobiles] = useState({});
  const [processingId, setProcessingId] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [payoutReference, setPayoutReference] = useState("");
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [reason, setReason] = useState("");
  const [toast, setToast] = useState(null);

  const notify = useCallback((message, type = "success") => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3800);
  }, []);

  const fetchRequests = useCallback(async ({ append = false, cursor = null } = {}) => {
    append ? setLoadingMore(true) : setLoading(true);
    setError("");

    try {
      const response = await api.post(
        "/admin/get-pep-redeem-requests",
        { status: activeStatus, limit: PAGE_SIZE, lastId: cursor },
        { skipAuthRedirect: true }
      );

      if (response.data?.status !== 100) {
        throw new Error(response.data?.message || "Unable to load redeem requests.");
      }

      const nextRequests = response.data.redeemRequests || [];
      setRequests((current) => (append ? [...current, ...nextRequests] : nextRequests));
      setLastId(response.data.lastId || null);
    } catch (requestError) {
      const isForbidden = requestError?.response?.status === 403;
      setError(isForbidden ? "Admin access required" : getMessage(requestError, "Unable to load redeem requests."));
      if (!append) setRequests([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeStatus]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filteredRequests = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return requests;

    return requests.filter((request) => {
      const user = request.user || {};
      return [user.userName, user.u_username, request.upiId]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [requests, search]);

  const closeDialog = (force = false) => {
    if (processingId && !force) return;
    setDialog(null);
    setPayoutReference("");
    setPaymentConfirmed(false);
    setReason("");
  };

  const openDialog = (action, request) => {
    setDialog({ action, request });
    setPayoutReference("");
    setPaymentConfirmed(false);
    setReason("");
  };

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      notify(`${label} copied`);
    } catch {
      notify(`Could not copy ${label.toLowerCase()}`, "error");
    }
  };

  const refreshHandledRow = async (redeemId) => {
    try {
      const response = await api.post(
        "/admin/get-pep-redeem-requests",
        { status: "all", limit: PAGE_SIZE, lastId: null },
        { skipAuthRedirect: true }
      );
      const fresh = response.data?.redeemRequests?.find((item) => item._id === redeemId);
      if (fresh) {
        setRequests((current) => activeStatus === "all" || fresh.status === activeStatus
          ? current.map((item) => item._id === redeemId ? fresh : item)
          : current.filter((item) => item._id !== redeemId));
      } else {
        setRequests((current) => current.filter((item) => item._id !== redeemId));
      }
    } catch {
      fetchRequests();
    }
  };

  const submitAction = async () => {
    if (!dialog) return;
    const { action, request } = dialog;
    setProcessingId(request._id);

    const body = { redeemId: request._id, action };
    if (action === "approved" && payoutReference.trim()) {
      body.payoutReference = payoutReference.trim();
    }
    if (action === "rejected" && reason.trim()) body.reason = reason.trim();

    try {
      const response = await api.post("/admin/update-pep-redeem-request", body, {
        skipAuthRedirect: true,
      });

      if (response.data?.status !== 100) {
        const statusError = new Error(response.data?.message || "Unable to update request.");
        statusError.bodyStatus = response.data?.status;
        throw statusError;
      }

      setRequests((current) => activeStatus === "all"
        ? current.map((item) => item._id === request._id ? response.data.redeemRequest : item)
        : current.filter((item) => item._id !== request._id));
      closeDialog(true);
      if (action === "approved") {
        notify(`Approved · ${formatMoney(request.finalAmount)}`);
      } else {
        notify(`Rejected · ${formatNumber(request.coins)} coins refunded`);
      }
    } catch (actionError) {
      const bodyStatus = actionError.bodyStatus || actionError?.response?.data?.status;
      const message = getMessage(actionError, "Unable to update request.");
      notify(message, "error");
      if (bodyStatus === 409) {
        closeDialog(true);
        await refreshHandledRow(request._id);
      }
    } finally {
      setProcessingId(null);
    }
  };

  const pendingCount = activeStatus === "pending" ? requests.length : null;
  const emptyText = activeStatus === "pending"
    ? "No pending redeem requests 🎉"
    : `No ${activeStatus === "all" ? "redeem" : activeStatus} requests`;

  return (
    <Page>
      <PageHeader>
        <BackButton type="button" onClick={() => nav("/admin/home")} aria-label="Back to admin home">
          <ArrowIcon aria-hidden="true">←</ArrowIcon> Back
        </BackButton>
        <TitleBlock>
          <Eyebrow>ADMIN · PAYOUTS</Eyebrow>
          <Title>PEP Coins Redeem Requests</Title>
          <Subtitle>Review the request, pay by UPI, then approve or reject it.</Subtitle>
        </TitleBlock>
        <RefreshButton type="button" onClick={() => fetchRequests()} disabled={loading}>
          <span aria-hidden="true">↻</span> Refresh
        </RefreshButton>
      </PageHeader>

      <Toolbar>
        <Tabs role="tablist" aria-label="Redeem request status">
          {STATUSES.map((status) => (
            <Tab
              type="button"
              role="tab"
              aria-selected={activeStatus === status}
              $active={activeStatus === status}
              key={status}
              onClick={() => setActiveStatus(status)}
            >
              {status[0].toUpperCase() + status.slice(1)}
              {status === "pending" && pendingCount !== null ? <Count>{pendingCount}</Count> : null}
            </Tab>
          ))}
        </Tabs>
        <SearchWrap>
          <SearchIcon aria-hidden="true">⌕</SearchIcon>
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search loaded requests"
            aria-label="Search by name, username or UPI ID"
          />
        </SearchWrap>
      </Toolbar>

      {loading ? (
        <StatePanel><Spinner /><span>Loading redeem requests...</span></StatePanel>
      ) : error ? (
        <StatePanel $error>
          <StateTitle>{error}</StateTitle>
          {error !== "Admin access required" ? (
            <SecondaryButton type="button" onClick={() => fetchRequests()}>Try again</SecondaryButton>
          ) : null}
        </StatePanel>
      ) : filteredRequests.length === 0 ? (
        <StatePanel>
          <StateTitle>{search ? "No loaded requests match your search" : emptyText}</StateTitle>
          <StateCopy>{search ? "Try a different name, username or UPI ID." : "There is nothing to review in this view."}</StateCopy>
        </StatePanel>
      ) : (
        <RequestList>
          {filteredRequests.map((request) => {
            const user = request.user;
            const isPending = request.status === "pending";
            const isRevealed = revealedMobiles[request._id];
            return (
              <RequestCard key={request._id}>
                <CardTop>
                  <UserBlock>
                    {user?.userProfilePic ? (
                      <Avatar src={user.userProfilePic} alt="" />
                    ) : (
                      <AvatarFallback aria-hidden="true">{user?.userName?.[0]?.toUpperCase() || "?"}</AvatarFallback>
                    )}
                    <UserText>
                      <NameLine>
                        <UserName>{user?.userName || "Deleted user"}</UserName>
                        {user?.isVerified ? <Verified title="Verified user">✓</Verified> : null}
                      </NameLine>
                      <Username>{user?.u_username ? `@${user.u_username}` : "Account unavailable"}</Username>
                      {user?.mobileNumber ? (
                        <RevealButton
                          type="button"
                          onClick={() => setRevealedMobiles((current) => ({ ...current, [request._id]: !isRevealed }))}
                        >
                          {isRevealed ? user.mobileNumber : maskMobile(user.mobileNumber)} · {isRevealed ? "Hide" : "Reveal"}
                        </RevealButton>
                      ) : null}
                    </UserText>
                  </UserBlock>
                  <StatusPill $status={request.status}>{request.status || "pending"}</StatusPill>
                </CardTop>

                <MoneyGrid>
                  <Metric><MetricLabel>Coins</MetricLabel><MetricValue>{formatNumber(request.coins || 0)}</MetricValue></Metric>
                  <Metric><MetricLabel>Coin value</MetricLabel><MetricValue>{formatMoney((request.coins || 0) / 10)}</MetricValue></Metric>
                  <Metric><MetricLabel>Fees</MetricLabel><MetricValue>{formatMoney(request.totalFees)}</MetricValue></Metric>
                  <Metric $primary><MetricLabel>Payout amount</MetricLabel><Payout>{formatMoney(request.finalAmount)}</Payout></Metric>
                </MoneyGrid>

                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>UPI ID</InfoLabel>
                    <UpiLine><InfoValue>{request.upiId || "—"}</InfoValue>{request.upiId ? <CopyButton type="button" onClick={() => copyText(request.upiId, "UPI ID")}>Copy</CopyButton> : null}</UpiLine>
                  </InfoItem>
                  <InfoItem><InfoLabel>Requested</InfoLabel><InfoValue>{formatDate(request.timestamp)}</InfoValue></InfoItem>
                  <InfoItem><InfoLabel>Current coin balance</InfoLabel><InfoValue>{user ? formatNumber(user.detCoins || 0) : "—"}</InfoValue></InfoItem>
                  {request.reviewedAt ? <InfoItem><InfoLabel>Reviewed</InfoLabel><InfoValue>{formatDate(request.reviewedAt)}</InfoValue></InfoItem> : null}
                  {request.payoutReference ? <InfoItem><InfoLabel>UTR / reference</InfoLabel><InfoValue>{request.payoutReference}</InfoValue></InfoItem> : null}
                  {request.rejectionReason ? <InfoItem $wide><InfoLabel>Rejection reason</InfoLabel><InfoValue>{request.rejectionReason}</InfoValue></InfoItem> : null}
                </InfoGrid>

                {isPending ? (
                  <Actions>
                    <RejectButton type="button" onClick={() => openDialog("rejected", request)} disabled={processingId === request._id}>Reject</RejectButton>
                    <ApproveButton type="button" onClick={() => openDialog("approved", request)} disabled={processingId === request._id}>Approve</ApproveButton>
                  </Actions>
                ) : null}
              </RequestCard>
            );
          })}
        </RequestList>
      )}

      {!loading && !error && lastId ? (
        <LoadMore type="button" onClick={() => fetchRequests({ append: true, cursor: lastId })} disabled={loadingMore}>
          {loadingMore ? "Loading..." : "Load more"}
        </LoadMore>
      ) : null}

      {dialog ? (
        <ModalBackdrop role="presentation" onMouseDown={(event) => event.target === event.currentTarget && closeDialog()}>
          <Dialog role="dialog" aria-modal="true" aria-labelledby="dialog-title">
            <DialogHeader>
              <div>
                <DialogEyebrow>{dialog.action === "approved" ? "CONFIRM PAYMENT" : "REJECT REQUEST"}</DialogEyebrow>
                <DialogTitle id="dialog-title">{dialog.action === "approved" ? "Approve redemption" : "Return the coins"}</DialogTitle>
              </div>
              <CloseButton type="button" onClick={closeDialog} aria-label="Close dialog">×</CloseButton>
            </DialogHeader>

            {dialog.action === "approved" ? (
              <>
                <PaymentSummary>
                  <SummaryAmount>{formatMoney(dialog.request.finalAmount)}</SummaryAmount>
                  <SummaryUpi>
                    <span>{dialog.request.upiId}</span>
                    <CopyButton type="button" onClick={() => copyText(dialog.request.upiId, "UPI ID")}>Copy</CopyButton>
                  </SummaryUpi>
                </PaymentSummary>
                <FieldLabel htmlFor="payout-reference">UTR / transaction reference <Optional>(optional)</Optional></FieldLabel>
                <TextInput id="payout-reference" value={payoutReference} onChange={(event) => setPayoutReference(event.target.value)} placeholder="Example: 426119830742" />
                <CheckLabel>
                  <Checkbox type="checkbox" checked={paymentConfirmed} onChange={(event) => setPaymentConfirmed(event.target.checked)} />
                  <span>I have sent {formatMoney(dialog.request.finalAmount)} to {dialog.request.upiId}.</span>
                </CheckLabel>
                <DialogActions>
                  <SecondaryButton type="button" onClick={closeDialog}>Cancel</SecondaryButton>
                  <ApproveButton type="button" onClick={submitAction} disabled={!paymentConfirmed || processingId === dialog.request._id}>
                    {processingId ? "Approving..." : "Confirm approval"}
                  </ApproveButton>
                </DialogActions>
              </>
            ) : (
              <>
                <RefundNote><strong>{formatNumber(dialog.request.coins)} coins</strong> will be returned to the user.</RefundNote>
                <FieldLabel>Reason <Optional>(optional)</Optional></FieldLabel>
                <ReasonOptions>
                  {QUICK_REASONS.map((quickReason) => (
                    <ReasonButton type="button" $selected={reason === quickReason} key={quickReason} onClick={() => setReason(quickReason)}>{quickReason}</ReasonButton>
                  ))}
                </ReasonOptions>
                <TextArea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Or enter a custom reason" rows="3" />
                <DialogActions>
                  <SecondaryButton type="button" onClick={closeDialog}>Cancel</SecondaryButton>
                  <RejectButton type="button" onClick={submitAction} disabled={processingId === dialog.request._id}>
                    {processingId ? "Rejecting..." : "Reject and refund"}
                  </RejectButton>
                </DialogActions>
              </>
            )}
          </Dialog>
        </ModalBackdrop>
      ) : null}

      {toast ? <Toast role="status" $type={toast.type}>{toast.message}</Toast> : null}
    </Page>
  );
}

const spin = keyframes`to { transform: rotate(360deg); }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); }`;

const Page = styled.div`
  min-height: 100vh; width: 100%; box-sizing: border-box; padding: 40px 24px 80px;
  background: #050505; color: #f7f7f7;
  @media (max-width: 640px) { padding: 24px 14px 64px; }
`;
const PageHeader = styled.header`
  width: min(1180px, 100%); margin: 0 auto 28px; display: grid;
  grid-template-columns: 150px 1fr 150px; align-items: center; gap: 20px;
  @media (max-width: 760px) { grid-template-columns: 1fr auto; align-items: start; }
`;
const TitleBlock = styled.div`text-align: center; @media (max-width: 760px) { grid-column: 1 / -1; grid-row: 1; text-align: left; }`;
const Eyebrow = styled.div`color: #8c8c8c; font-size: 11px; font-weight: 800; letter-spacing: 0.12em;`;
const Title = styled.h1`margin: 7px 0 0; font-size: clamp(27px, 4vw, 42px); line-height: 1.1; letter-spacing: 0;`;
const Subtitle = styled.p`margin: 10px 0 0; color: #a8a8a8; font-size: 14px; line-height: 1.5;`;
const BaseButton = styled.button`
  min-height: 42px; border: 1px solid #3a3a3a; padding: 0 16px; background: #111; color: #fff;
  font: inherit; font-size: 13px; font-weight: 800; cursor: pointer; transition: border-color 160ms ease, background 160ms ease, color 160ms ease, transform 160ms ease;
  &:hover:not(:disabled) { border-color: #777; transform: translateY(-1px); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;
const BackButton = styled(BaseButton)`justify-self: start; display: flex; align-items: center; gap: 8px; @media (max-width: 760px) { grid-row: 2; }`;
const ArrowIcon = styled.span`font-size: 18px; line-height: 1;`;
const RefreshButton = styled(BaseButton)`justify-self: end; display: flex; align-items: center; gap: 7px; @media (max-width: 760px) { grid-row: 2; }`;
const Toolbar = styled.div`
  width: min(1180px, 100%); margin: 0 auto 18px; display: flex; align-items: center; justify-content: space-between; gap: 16px;
  @media (max-width: 760px) { align-items: stretch; flex-direction: column; }
`;
const Tabs = styled.div`display: flex; max-width: 100%; overflow-x: auto; border-bottom: 1px solid #282828;`;
const Tab = styled.button`
  position: relative; min-height: 46px; padding: 0 17px; border: 0; border-bottom: 2px solid ${({ $active }) => $active ? "#fff" : "transparent"};
  background: transparent; color: ${({ $active }) => $active ? "#fff" : "#777"}; font: inherit; font-size: 13px; font-weight: 800; cursor: pointer; white-space: nowrap;
`;
const Count = styled.span`margin-left: 7px; padding: 2px 7px; border-radius: 10px; background: #fff; color: #050505; font-size: 10px;`;
const SearchWrap = styled.div`position: relative; width: min(300px, 100%);`;
const SearchIcon = styled.span`position: absolute; left: 13px; top: 50%; transform: translateY(-53%); color: #777; font-size: 20px;`;
const SearchInput = styled.input`
  width: 100%; height: 44px; box-sizing: border-box; padding: 0 14px 0 40px; border: 1px solid #303030; background: #0d0d0d; color: #fff; font: inherit; outline: none;
  &:focus { border-color: #777; } &::placeholder { color: #666; }
`;
const StatePanel = styled.div`
  width: min(1180px, 100%); min-height: 260px; box-sizing: border-box; margin: 0 auto; border: 1px solid ${({ $error }) => $error ? "#522" : "#252525"};
  background: #0a0a0a; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 14px; color: #9d9d9d; text-align: center; padding: 30px;
`;
const StateTitle = styled.h2`margin: 0; color: #eee; font-size: 19px; letter-spacing: 0;`;
const StateCopy = styled.p`margin: 0; font-size: 13px;`;
const Spinner = styled.span`width: 28px; height: 28px; border: 3px solid #282828; border-top-color: #fff; border-radius: 50%; animation: ${spin} 0.75s linear infinite;`;
const RequestList = styled.main`width: min(1180px, 100%); margin: 0 auto; display: grid; gap: 14px;`;
const RequestCard = styled.article`
  border: 1px solid #292929; background: #0b0b0b; padding: 22px; animation: ${slideUp} 280ms ease both;
  @media (max-width: 560px) { padding: 17px; }
`;
const CardTop = styled.div`display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;`;
const UserBlock = styled.div`display: flex; align-items: center; gap: 13px; min-width: 0;`;
const Avatar = styled.img`width: 48px; height: 48px; flex: 0 0 48px; object-fit: cover; border-radius: 50%; border: 1px solid #333;`;
const AvatarFallback = styled.div`width: 48px; height: 48px; flex: 0 0 48px; display: grid; place-items: center; border-radius: 50%; background: #242424; color: #ddd; font-size: 18px; font-weight: 800;`;
const UserText = styled.div`min-width: 0;`;
const NameLine = styled.div`display: flex; align-items: center; gap: 7px;`;
const UserName = styled.h2`margin: 0; font-size: 16px; overflow-wrap: anywhere; letter-spacing: 0;`;
const Verified = styled.span`width: 16px; height: 16px; flex: 0 0 16px; border-radius: 50%; display: grid; place-items: center; background: #4f7cff; color: #fff; font-size: 10px;`;
const Username = styled.div`margin-top: 3px; color: #898989; font-size: 12px;`;
const RevealButton = styled.button`margin: 5px 0 0; padding: 0; border: 0; background: none; color: #bdbdbd; font: inherit; font-size: 11px; cursor: pointer;`;
const StatusPill = styled.span`
  flex: 0 0 auto; padding: 6px 10px; border: 1px solid ${({ $status }) => $status === "approved" ? "#256d45" : $status === "rejected" ? "#7f3030" : "#7a5e24"};
  background: ${({ $status }) => $status === "approved" ? "#10281c" : $status === "rejected" ? "#2b1111" : "#2c2411"};
  color: ${({ $status }) => $status === "approved" ? "#74dda4" : $status === "rejected" ? "#ff9999" : "#e9c46a"}; font-size: 11px; font-weight: 800; text-transform: capitalize;
`;
const MoneyGrid = styled.div`
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-top: 20px; border: 1px solid #242424;
  @media (max-width: 700px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;
const Metric = styled.div`
  min-width: 0; padding: 15px; border-right: 1px solid #242424; background: ${({ $primary }) => $primary ? "#151515" : "#0e0e0e"};
  &:last-child { border-right: 0; }
  @media (max-width: 700px) { &:nth-child(2) { border-right: 0; } &:nth-child(-n + 2) { border-bottom: 1px solid #242424; } }
`;
const MetricLabel = styled.div`margin-bottom: 7px; color: #727272; font-size: 10px; font-weight: 800; text-transform: uppercase;`;
const MetricValue = styled.div`font-size: 16px; font-weight: 700; overflow-wrap: anywhere;`;
const Payout = styled(MetricValue)`font-size: 20px; color: #fff;`;
const InfoGrid = styled.div`display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px 24px; padding-top: 19px; @media (max-width: 700px) { grid-template-columns: 1fr 1fr; } @media (max-width: 480px) { grid-template-columns: 1fr; gap: 14px; }`;
const InfoItem = styled.div`min-width: 0; grid-column: ${({ $wide }) => $wide ? "1 / -1" : "auto"};`;
const InfoLabel = styled.div`margin-bottom: 5px; color: #686868; font-size: 10px; font-weight: 800; text-transform: uppercase;`;
const InfoValue = styled.div`color: #d7d7d7; font-size: 13px; overflow-wrap: anywhere;`;
const UpiLine = styled.div`display: flex; align-items: center; gap: 9px; min-width: 0;`;
const CopyButton = styled.button`padding: 4px 8px; border: 1px solid #3a3a3a; background: #151515; color: #d7d7d7; font: inherit; font-size: 10px; font-weight: 800; cursor: pointer;`;
const Actions = styled.div`display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; padding-top: 18px; border-top: 1px solid #242424;`;
const ApproveButton = styled(BaseButton)`background: #f3f3f3; color: #050505; border-color: #f3f3f3; min-width: 112px; &:hover:not(:disabled) { background: #fff; border-color: #fff; }`;
const RejectButton = styled(BaseButton)`background: #1a0c0c; color: #ff9a9a; border-color: #673030; min-width: 100px; &:hover:not(:disabled) { background: #2a1010; border-color: #9d4545; }`;
const SecondaryButton = styled(BaseButton)`background: transparent;`;
const LoadMore = styled(BaseButton)`display: block; min-width: 150px; margin: 22px auto 0;`;
const ModalBackdrop = styled.div`position: fixed; z-index: 1000; inset: 0; display: grid; place-items: center; padding: 20px; background: rgba(0, 0, 0, 0.82); backdrop-filter: blur(5px);`;
const Dialog = styled.div`width: min(500px, 100%); max-height: calc(100vh - 40px); overflow-y: auto; box-sizing: border-box; padding: 24px; border: 1px solid #3a3a3a; background: #0d0d0d; box-shadow: 0 24px 80px rgba(0,0,0,0.7); animation: ${slideUp} 180ms ease; @media (max-width: 520px) { padding: 19px; }`;
const DialogHeader = styled.div`display: flex; justify-content: space-between; gap: 20px; margin-bottom: 21px;`;
const DialogEyebrow = styled.div`color: #747474; font-size: 10px; font-weight: 800; letter-spacing: 0.12em;`;
const DialogTitle = styled.h2`margin: 6px 0 0; font-size: 23px; letter-spacing: 0;`;
const CloseButton = styled.button`width: 34px; height: 34px; flex: 0 0 34px; border: 1px solid #333; background: transparent; color: #aaa; font-size: 24px; line-height: 1; cursor: pointer;`;
const PaymentSummary = styled.div`padding: 19px; margin-bottom: 19px; border: 1px solid #2f2f2f; background: #151515;`;
const SummaryAmount = styled.div`font-size: 30px; font-weight: 800;`;
const SummaryUpi = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 14px; margin-top: 9px; color: #aaa; font-size: 13px; overflow-wrap: anywhere;`;
const FieldLabel = styled.label`display: block; margin: 16px 0 7px; color: #d0d0d0; font-size: 12px; font-weight: 800;`;
const Optional = styled.span`color: #6e6e6e; font-weight: 500;`;
const TextInput = styled.input`width: 100%; height: 45px; box-sizing: border-box; padding: 0 13px; border: 1px solid #3a3a3a; background: #080808; color: #fff; font: inherit; outline: none; &:focus { border-color: #888; }`;
const CheckLabel = styled.label`display: flex; align-items: flex-start; gap: 11px; margin-top: 19px; padding: 14px; border: 1px solid #303030; background: #111; color: #d5d5d5; font-size: 13px; line-height: 1.5; cursor: pointer;`;
const Checkbox = styled.input`width: 17px; height: 17px; flex: 0 0 17px; margin: 1px 0 0; accent-color: #fff;`;
const DialogActions = styled.div`display: flex; justify-content: flex-end; gap: 10px; margin-top: 22px; @media (max-width: 420px) { > button { flex: 1; min-width: 0; padding: 0 10px; } }`;
const RefundNote = styled.div`padding: 14px; border: 1px solid #472727; background: #1b0d0d; color: #d8adad; font-size: 13px;`;
const ReasonOptions = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const ReasonButton = styled.button`padding: 8px 10px; border: 1px solid ${({ $selected }) => $selected ? "#aaa" : "#3a3a3a"}; background: ${({ $selected }) => $selected ? "#eee" : "#151515"}; color: ${({ $selected }) => $selected ? "#050505" : "#c4c4c4"}; font: inherit; font-size: 11px; font-weight: 700; cursor: pointer;`;
const TextArea = styled.textarea`width: 100%; box-sizing: border-box; margin-top: 10px; padding: 12px; resize: vertical; border: 1px solid #3a3a3a; background: #080808; color: #fff; font: inherit; outline: none; &:focus { border-color: #888; }`;
const Toast = styled.div`position: fixed; z-index: 1100; right: 22px; bottom: 22px; max-width: min(380px, calc(100vw - 28px)); box-sizing: border-box; padding: 14px 17px; border: 1px solid ${({ $type }) => $type === "error" ? "#763838" : "#356345"}; background: ${({ $type }) => $type === "error" ? "#220e0e" : "#0f2116"}; color: ${({ $type }) => $type === "error" ? "#ffadad" : "#a7e8bc"}; font-size: 13px; font-weight: 800; box-shadow: 0 15px 50px #000; animation: ${slideUp} 180ms ease; @media (max-width: 520px) { right: 14px; bottom: 14px; }`;
